import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Trash2, FolderPlus, Loader2, Check, ArrowRightLeft, FileSpreadsheet } from "lucide-react";
import BudgetExcelImportDialog from "@/components/BudgetExcelImportDialog";
import { DateCell } from "@/components/DateCell";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const TPL_LABEL = { onstage: "OnStage", dining: "Dining", empty: "Simplificado" };
const cell = "h-8 w-full bg-transparent border border-transparent hover:border-slate-300 rounded-md px-2 text-sm transition-colors focus:outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary/20";
const cellNum = cell + " text-right tabular-nums";

const lineTotal = (l) => (Number(l.unit_price) || 0) * (Number(l.units) || 0);
const lineCost = (l, pct) => { const t = lineTotal(l); return l.keep_full ? t : t / (1 + (Number(pct) || 0) / 100); };

const Spinner = () => <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

export default function SimpleBudget({ projectId, mode = "manage", onReset }) {
  const ro = mode === "view";
  const canManage = mode === "manage";
  const [sections, setSections] = useState([]);
  const [pct, setPct] = useState(16);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewCierre, setViewCierre] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const [volcando, setVolcando] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const loadedRef = useRef(false);
  const timerRef = useRef(null);

  const load = () => api.get(`/projects/${projectId}/simple-budget`).then((r) => {
    setSections(r.data.sections || []);
    setPct(r.data.cierre_pct != null ? r.data.cierre_pct : 16);
    setTemplate(r.data.template || null);
  });

  useEffect(() => {
    let alive = true;
    api.get(`/projects/${projectId}/simple-budget`).then((r) => {
      if (!alive) return;
      setSections(r.data.sections || []);
      setPct(r.data.cierre_pct != null ? r.data.cierre_pct : 16);
      setTemplate(r.data.template || null);
      setLoading(false);
      setTimeout(() => { loadedRef.current = true; }, 120);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    if (!loadedRef.current || ro) return;
    setSaveState("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaveState("saving");
      try { await api.put(`/projects/${projectId}/simple-budget`, { sections, cierre_pct: Number(pct) || 0, template }); setSaveState("saved"); }
      catch (e) { setSaveState("dirty"); }
    }, 1000);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, pct]);

  const setLine = (si, li, patch) => setSections((p) => { const s = [...p]; s[si] = { ...s[si], lines: s[si].lines.map((l, i) => i === li ? { ...l, ...patch } : l) }; return s; });
  const addLine = (si) => setSections((p) => { const s = [...p]; s[si] = { ...s[si], lines: [...s[si].lines, { id: uid(), concept: "", unit_price: 0, units: 0, notes: "", keep_full: false, tipologia: "interna", fecha_prevista_pago: null, margen_pct: 0 }] }; return s; });
  const delLine = (si, li) => setSections((p) => { const s = [...p]; s[si] = { ...s[si], lines: s[si].lines.filter((_, i) => i !== li) }; return s; });
  const setSecName = (si, name) => setSections((p) => p.map((s, i) => i === si ? { ...s, name } : s));
  const addSection = () => setSections((p) => [...p, { id: uid(), name: "Nueva sección", lines: [] }]);
  const delSection = (si) => { if (!window.confirm("¿Eliminar la sección y sus líneas?")) return; setSections((p) => p.filter((_, i) => i !== si)); };

  const grand = sections.reduce((a, s) => a + s.lines.reduce((b, l) => b + (viewCierre ? lineCost(l, pct) : lineTotal(l)), 0), 0);
  const grandClient = sections.reduce((a, s) => a + s.lines.reduce((b, l) => b + lineTotal(l), 0), 0);
  const grandCost = sections.reduce((a, s) => a + s.lines.reduce((b, l) => b + lineCost(l, pct), 0), 0);

  const volcar = async () => {
    setVolcando(true);
    try { const r = await api.post(`/projects/${projectId}/simple-budget/volcar`); const n = r.data.externos || 0; toast.success(`Volcados ${r.data.created} gastos previstos${n ? ` · ${n} ${n === 1 ? "externo" : "externos"} como previsión con su fecha de pago` : ""}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sin permiso para crear gastos"); }
    finally { setVolcando(false); }
  };

  const resetTemplate = async () => {
    if (!window.confirm("¿Reiniciar el presupuesto? Se borrará esta hoja y podrás elegir otra plantilla.")) return;
    try { await api.delete(`/projects/${projectId}/simple-budget`); onReset && onReset(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4" data-testid="simple-budget">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge className="bg-navy/10 text-navy border-transparent">{TPL_LABEL[template] || "Simplificado"}</Badge>
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            {saveState === "saving" ? <><Loader2 className="h-3 w-3 animate-spin" /> Guardando…</> : saveState === "dirty" ? "Sin guardar" : <><Check className="h-3 w-3 text-primary" /> Guardado</>}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch id="view-cierre" checked={viewCierre} onCheckedChange={setViewCierre} data-testid="simple-cierre-toggle" />
            <Label htmlFor="view-cierre" className="text-sm font-normal cursor-pointer">Ver a coste (cierre)</Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-normal text-muted-foreground">Margen cierre</Label>
            <input type="number" value={pct} disabled={ro} onChange={(e) => setPct(e.target.value)} className="h-8 w-16 rounded-md border border-border px-2 text-sm text-right tabular-nums" data-testid="simple-cierre-pct" />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
          {!ro && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)} data-testid="simple-import-btn"><FileSpreadsheet className="h-4 w-4" /> Importar Excel</Button>}
          {!ro && <Button size="sm" variant="outline" className="gap-1.5" onClick={volcar} disabled={volcando} data-testid="simple-volcar-btn">{volcando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />} Volcar a gastos</Button>}
          {canManage && <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={resetTemplate} data-testid="simple-reset-btn">Cambiar plantilla</Button>}
          {canManage && <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive" onClick={resetTemplate} data-testid="simple-delete-btn"><Trash2 className="h-4 w-4" /> Eliminar presupuesto</Button>}
        </div>
      </div>

      {viewCierre && <div className="text-xs rounded-md bg-gold/10 text-[#a9741c] px-3 py-2">Vista de <b>cierre</b>: importes a coste (precio sin el {Number(pct) || 0}% de margen), salvo restaurantes/shows marcados como «Rest.», que se mantienen a precio completo.</div>}

      {sections.map((sec, si) => {
        const secTotal = sec.lines.reduce((b, l) => b + (viewCierre ? lineCost(l, pct) : lineTotal(l)), 0);
        return (
          <Card key={sec.id} className="border-border shadow-none overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 border-b border-border">
              <input className={cn(cell, "font-heading font-bold text-sm max-w-md")} value={sec.name} disabled={ro} onChange={(e) => setSecName(si, e.target.value)} data-testid={`simple-sec-name-${si}`} />
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{eur(secTotal)}</span>
                {!ro && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => delSection(si)} data-testid={`simple-del-sec-${si}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1040px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left border-b border-border bg-muted/20">
                    <th className="px-2 py-2 font-semibold w-[26%]">Concepto</th>
                    <th className="px-2 py-2 font-semibold text-right w-24">Precio €</th>
                    <th className="px-2 py-2 font-semibold text-right w-16">Uds</th>
                    <th className="px-2 py-2 font-semibold text-right w-28">{viewCierre ? "Coste" : "Total"}</th>
                    <th className="px-2 py-2 font-semibold w-40">Tipología</th>
                    <th className="px-2 py-2 font-semibold w-36" title="Solo para gastos externos">Fecha prevista pago</th>
                    <th className="px-2 py-2 font-semibold text-right w-20" title="Solo para gastos externos">Margen %</th>
                    <th className="px-2 py-2 font-semibold text-center w-14" title="Restaurante/show: mantiene el precio completo en el cierre">Rest.</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {sec.lines.map((l, li) => {
                    const amt = viewCierre ? lineCost(l, pct) : lineTotal(l);
                    const esExterno = l.tipologia === "externo";
                    return (
                      <tr key={l.id} className="border-b border-border/60 last:border-0 hover:bg-muted/20 transition-colors" data-testid={`simple-line-${si}-${li}`}>
                        <td className="p-1">
                          <input className={cell} value={l.concept} disabled={ro} onChange={(e) => setLine(si, li, { concept: e.target.value })} placeholder="Concepto" data-testid={`simple-concept-${si}-${li}`} />
                          {l.notes ? <div className="text-[11px] text-muted-foreground px-2 truncate" title={l.notes}>{l.notes}</div> : null}
                        </td>
                        <td className="p-1"><input type="number" className={cellNum} value={l.unit_price} disabled={ro} onChange={(e) => setLine(si, li, { unit_price: e.target.value })} data-testid={`simple-price-${si}-${li}`} /></td>
                        <td className="p-1"><input type="number" className={cellNum} value={l.units} disabled={ro} onChange={(e) => setLine(si, li, { units: e.target.value })} data-testid={`simple-units-${si}-${li}`} /></td>
                        <td className={cn("p-1 text-right tabular-nums font-medium whitespace-nowrap", viewCierre && !l.keep_full && "text-primary")}>{eur(amt)}</td>
                        <td className="p-1">
                          <select className={cell} value={l.tipologia || "interna"} disabled={ro} onChange={(e) => setLine(si, li, { tipologia: e.target.value })} data-testid={`simple-tipologia-${si}-${li}`}>
                            <option value="interna">Horas internas</option>
                            <option value="externo">Gasto externo</option>
                          </select>
                        </td>
                        <td className="p-1">
                          {esExterno
                            ? <DateCell value={l.fecha_prevista_pago || ""} disabled={ro} onChange={(v) => setLine(si, li, { fecha_prevista_pago: v })} testid={`simple-fecha-${si}-${li}`} placeholder="Fecha pago…" />
                            : <span className="text-xs text-muted-foreground/50 px-2">—</span>}
                        </td>
                        <td className="p-1 text-right">
                          {esExterno
                            ? <input type="number" className={cellNum} value={l.margen_pct ?? 0} disabled={ro} onChange={(e) => setLine(si, li, { margen_pct: e.target.value })} data-testid={`simple-margen-${si}-${li}`} />
                            : <span className="text-xs text-muted-foreground/50 px-2">—</span>}
                        </td>
                        <td className="p-1 text-center"><input type="checkbox" checked={!!l.keep_full} disabled={ro} onChange={(e) => setLine(si, li, { keep_full: e.target.checked })} className="h-4 w-4 accent-navy cursor-pointer" data-testid={`simple-keepfull-${si}-${li}`} /></td>
                        <td className="p-1">{!ro && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => delLine(si, li)} data-testid={`simple-del-line-${si}-${li}`}><Trash2 className="h-3.5 w-3.5" /></Button>}</td>
                      </tr>
                    );
                  })}
                  {sec.lines.length === 0 && <tr><td colSpan={9} className="px-2 py-4 text-center text-xs text-muted-foreground">Sin partidas.</td></tr>}
                </tbody>
              </table>
            </div>
            {!ro && <div className="px-3 py-2 border-t border-border"><Button size="sm" variant="ghost" className="text-primary gap-1.5" onClick={() => addLine(si)} data-testid={`simple-add-line-${si}`}><Plus className="h-4 w-4" /> Añadir partida</Button></div>}
          </Card>
        );
      })}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {!ro && <Button variant="outline" className="gap-1.5" onClick={addSection} data-testid="simple-add-section"><FolderPlus className="h-4 w-4" /> Añadir sección</Button>}
        <div className="ml-auto text-right">
          <div className="text-sm text-muted-foreground">{viewCierre ? "Total a coste (cierre)" : "Total presupuesto (cliente)"}</div>
          <div className="text-2xl font-heading font-bold tabular-nums" data-testid="simple-grand-total">{eur(grand)}</div>
          {!viewCierre
            ? <div className="text-xs text-muted-foreground">Coste de cierre: <span className="tabular-nums">{eur(grandCost)}</span></div>
            : <div className="text-xs text-muted-foreground">Precio cliente: <span className="tabular-nums">{eur(grandClient)}</span></div>}
        </div>
      </div>

      <BudgetExcelImportDialog projectId={projectId} open={importOpen} onOpenChange={setImportOpen} onImported={load} />
    </div>
  );
}
